import type {
  InteractiveContent,
  ScaleResult,
  ScalesStaticContent,
} from '@core-api/presentation/types/response';
import { useFetchScalesResults } from '@mentimeter/presentation-data-hooks/fetch-scales-results';
import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { useMemo } from 'react';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../../legacy-context/presentationContext';
import { getVisualizationState } from '../../../utils/getVisualizationState';
import { useScreenshotReady } from '../../../shared/hooks/use-screenshot-ready';
import type { ScalesRange } from '../types';
import {
  SCALES_FALLBACK_MIN_RANGE,
  SCALES_FALLBACK_MAX_RANGE,
} from '../utils/constants';
import { useScalesPreviewResults } from './use-scales-preview-results';

export interface UseScalesPresentationReturn {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  visualizationType: 'slider-chart' | 'spider-chart';
  choices: InteractiveContent['choices'];
  range: ScalesRange;
  dimensionLabels: { min: string; max: string };
  results: ScaleResult[];
  shouldHideResults: boolean;
  shouldShowOverallAverage: boolean;
  shouldAnimate: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
}

export function useScalesPresentation(): UseScalesPresentationReturn {
  const {
    useCurrentSession,
    useConfig,
    usePace,
    InteractiveCanvasComponents,
    isPreviewResultsEnabled,
  } = usePresentationContext();

  const slide = usePresentationSlideContext();

  const { currentStep } = usePace();

  const { enableAnimations, mode } = useConfig();
  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();

  const interactiveContent = slide.interactiveContents[0]!;

  const staticContent = slide.staticContent as ScalesStaticContent;

  const { data, isLoading } = useFetchScalesResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !shouldHideResults && !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const range = useMemo(
    () => ({
      min: interactiveContent.responseRange?.min ?? SCALES_FALLBACK_MIN_RANGE,
      max: interactiveContent.responseRange?.max ?? SCALES_FALLBACK_MAX_RANGE,
    }),
    [interactiveContent.responseRange],
  );

  const { results: previewResults } = useScalesPreviewResults({
    isEnabled: isPreviewResultsEnabled,
    choices: interactiveContent.choices,
    range,
  });

  useScreenshotReady({ isReady: mode === 'screenshot' && !isLoading });

  return {
    title: slide.title,
    styledTitle: staticContent.styledTitle as Record<string, unknown>,
    label: staticContent.label,
    longerDescription: interactiveContent.description,
    fontSizeOffset: staticContent.textSizeOffset,
    visualizationType: staticContent.interactiveContentStyling.visualization,
    choices: interactiveContent.choices,
    range,
    dimensionLabels: {
      min: staticContent.interactiveContentStyling.responseRangeLabels
        .minimumValueLabel,
      max: staticContent.interactiveContentStyling.responseRangeLabels
        .maximumValueLabel,
    },
    results: isPreviewResultsEnabled ? previewResults : (data?.results ?? []),
    shouldHideResults,
    shouldShowOverallAverage:
      staticContent.interactiveContentStyling.showAverage,
    shouldAnimate: enableAnimations,
    CanvasVisualizationArea:
      InteractiveCanvasComponents?.CanvasVisualizationArea,
  };
}
